import type { IEventService } from '@realitycollective/service-framework';
import { createServiceToken } from '@realitycollective/service-framework';
import type { AssistConsentState } from '../../core/assistConsentMachine';
import type { AudioTile } from '../../core/wearerAudioPolicy';
import type { StatusSeverity } from '../../core/serverAvailabilityModel';

export type AssistSessionEventMap = {
  /** Raised on every consent transition. */
  stateChange: AssistConsentState;
  /**
   * Raised when the microphone's EFFECTIVE state changes - including when
   * consent moved it rather than the wearer, so a tile bound to this can never
   * drift out of step with the mic.
   */
  microphoneLiveChanged: boolean;
  /** One compact line for every leg the SFU reports (D10's out-of-band failure signal). */
  qualityChanged: { readonly line: string; readonly severity: StatusSeverity };
  /** The hand menu's tiles should re-render (consent, mute, or roster changed). */
  tilesChanged: undefined;
};

/**
 * Owns the consent state machine (Plan-01 step 6, RA-C6 / D5 / D6): wires the
 * machine's transitions to their consequences - mic publish/mute (at once on
 * a pause), roster subscriptions, the presence broadcast - and exposes the
 * hand-menu tile states. Port of the Unity `IAssistSessionService`.
 */
export interface IAssistSessionService extends IEventService<AssistSessionEventMap> {
  /** The machine's current state. */
  readonly state: AssistConsentState;

  /** The hand menu should offer "Allow assistance". */
  readonly awaitingAllow: boolean;

  /** The hand menu's master action (Allow / Pause / Resume). */
  masterAction(): void;

  /**
   * The wearer has muted their OWN microphone. Distinct from a pause: this
   * silences the wearer without touching consent, assistant subscriptions, or
   * the panels - the assistant keeps talking and the session stays live.
   */
  readonly microphoneMuted: boolean;

  /**
   * Is a microphone actually open right now? Consent AND the wearer's own mute
   * both have to allow it, so this is what a mic indicator must show - never
   * `microphoneMuted` on its own.
   */
  readonly microphoneLive: boolean;

  /** Set the wearer's own microphone mute. */
  setMicrophoneMuted(muted: boolean): void;

  /** Flip the wearer's own microphone mute (the hand-menu tile). */
  toggleMicrophoneMuted(): void;

  /**
   * The remote config's `assist.autoAllow` arrived (RA-C10/D6). Applies to
   * the next session start; a session already WAITING for consent is promoted
   * when it flips to true. Null = config silent - the configured default
   * stays in force.
   */
  applyRemoteAutoAllow(autoAllow: boolean | null): void;

  /** The wearer's own microphone tile, as it should render right now. */
  readonly micTile: AudioTile;

  /** The "can I hear the assistant" tile, as it should render right now. */
  readonly assistantAudioTile: AudioTile;
}

export const IAssistSessionService = createServiceToken<IAssistSessionService>('IAssistSessionService');
